import { injectable } from 'inversify';
import { IOrganisationForSearch, PublicOrganisationForSearchClient } from '@studyportals/domain-client';
import { Cache } from '@/platform/Cache';
import { CacheKeyOrganisationForSearch } from '@studyportals/search-page-interface';
import { ErrorReporter } from '@/infrastructure/ErrorReporter';
import { Task } from '@/platform/Task';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { RequestInitiatedEvent } from '@/platform/events/RequestInitiatedEvent';
import { RequestCompletedEvent } from '@/platform/events/RequestCompletedEvent';

@injectable()
export class DomainApiProjectionOrganisationForSearch {
	private errorReporter: ErrorReporter;

	private readonly client: PublicOrganisationForSearchClient;
	private readonly cache: Cache;
	private readonly cacheBuildingTasksByKey: Map<string, Task<IOrganisationForSearch | null>> = new Map();
	private readonly eventAggregationService: EventAggregationService;

	public constructor(errorReporter: ErrorReporter, cache: Cache, eventAggregationService: EventAggregationService) {
		this.errorReporter = errorReporter;
		this.cache = cache;
		this.eventAggregationService = eventAggregationService;
		this.client = new PublicOrganisationForSearchClient(CONFIG.DOMAIN_API_URL, undefined, 1);
	}

	public async exists(id: string): Promise<boolean> {
		return (await this.retrieveOrNull(id)) !== null;
	}

	public async retrieveOrNull(id: string): Promise<IOrganisationForSearch | null> {
		const cacheKey = new CacheKeyOrganisationForSearch(id);

		const cachedOrganisation = this.cache.get<IOrganisationForSearch>(cacheKey.value);
		if (cachedOrganisation !== null) {
			return cachedOrganisation;
		}

		const cacheBuildingTaskOngoing = this.cacheBuildingTasksByKey.get(cacheKey.value);
		if (cacheBuildingTaskOngoing !== undefined) {
			return await cacheBuildingTaskOngoing.promise;
		}

		const cacheBuildingTask = new Task<IOrganisationForSearch | null>();
		this.cacheBuildingTasksByKey.set(cacheKey.value, cacheBuildingTask);

		void this.doRetrieve(id, cacheBuildingTask);

		const organisation = await cacheBuildingTask.promise;
		this.cache.set<IOrganisationForSearch | null>(cacheKey.value, organisation);
		this.cacheBuildingTasksByKey.delete(cacheKey.value);

		return organisation;
	}

	private async doRetrieve(id: string, task: Task<IOrganisationForSearch | null>): Promise<void> {
		this.eventAggregationService.publish(new RequestInitiatedEvent());

		try {
			task.resolveWith(await this.client.fetch(+id));
		} catch (e) {
			this.errorReporter.report(e);

			task.resolveWith(null);
		} finally {
			this.eventAggregationService.publish(new RequestCompletedEvent());
		}
	}
}
